import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportLayout, ExportScheduleForm } from '../reports/reports.types';
import {
  ReportLayoutQueryParams,
  ReportLayoutChart,
  ReportLayoutRelatedToType,
  ReportLayoutListResponse,
  ReportLayoutListExportResponse,
} from './reporting.types';
import { PaginatedResource } from '../../shared/models/paginated-resource';
import { BaseService } from '@services/base.service';
import { reportBuilderListMenuItems } from './reporting.constants';
import { sortChartData } from './reporting.functions';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';

@Injectable({ providedIn: 'root' })
export class ReportingService extends BaseService {
  constructor(
    protected readonly httpClient: HttpClient,
    private readonly store: Store,
  ) {
    super(httpClient);
  }

  create<T>(model: Partial<ReportLayout<T>>): Observable<ReportLayout<T>> {
    const payload = this.mapOutgoingLayout(model);
    return this.httpClient
      .post<{ report_layout: ReportLayout<T> }>(`report_layouts`, {
        report_layout: this.transformAttributeProp(payload),
      })
      .pipe(
        map(({ report_layout }) => report_layout),
        map((r) => this.mapIncomingLayout(r)),
      );
  }

  update<T>(model: Partial<ReportLayout<T>>): Observable<ReportLayout<T>> {
    const payload = this.mapOutgoingLayout(model);
    return this.httpClient
      .patch<{ report_layout: ReportLayout<T> }>(`report_layouts/${model.id}`, {
        report_layout: this.transformAttributeProp(payload),
      })
      .pipe(
        map(({ report_layout }) => report_layout),
        map((r) => this.mapIncomingLayout(r)),
      );
  }

  duplicate<T>(id: number): Observable<ReportLayout<T>> {
    return this.httpClient.put<{ report_layout: ReportLayout<T> }>(`report_layouts/${id}/duplicate`, null).pipe(
      map(({ report_layout }) => report_layout),
      map((r) => this.mapIncomingLayout(r)),
    );
  }

  listSchedules(reportLayoutId: number): Observable<ExportScheduleForm[]> {
    return this.httpClient.get<{ schedules: ExportScheduleForm[] }>(`report_layouts/${reportLayoutId}/schedules`).pipe(
      map((response) => response?.schedules ?? []),
      map((schedules) => schedules.map((s) => this.mapIncomingSchedule(s))),
    );
  }

  createSchedule(reportLayoutId: number, schedule: any): Observable<ExportScheduleForm> {
    const payload = this.mapOutgoingSchedule(schedule);
    return this.httpClient
      .post<{ schedule: ExportScheduleForm }>(`report_layouts/${reportLayoutId}/schedules`, { schedule: payload })
      .pipe(
        map((response) => response.schedule),
        map((s) => this.mapIncomingSchedule(s)),
      );
  }

  updateSchedule(reportLayoutId: number, scheduleId: number, schedule: any): Observable<ExportScheduleForm> {
    const payload = this.mapOutgoingSchedule(schedule);
    return this.httpClient
      .patch<{
        schedule: ExportScheduleForm;
      }>(`report_layouts/${reportLayoutId}/schedules/${scheduleId}`, { schedule: payload })
      .pipe(
        map((response) => response.schedule),
        map((s) => this.mapIncomingSchedule(s)),
      );
  }

  deleteSchedule(reportLayoutId: number, scheduleId: number): Observable<void> {
    return this.httpClient.delete<void>(`report_layouts/${reportLayoutId}/schedules/${scheduleId}`);
  }

  getReports<T>(filter: ReportLayoutQueryParams): Observable<PaginatedResource<ReportLayout<T>>> {
    let params = new HttpParams();

    Object.keys(filter).forEach((key) => {
      params = params.append(key, filter[key]);
    });

    return this.httpClient.get<PaginatedResource<ReportLayout<T>>>(`report_layouts`, { params }).pipe(
      map((response) => {
        if (response && response.results) {
          response.results = response.results.map((r) => this.mapIncomingLayout(r));
        }
        return response;
      }),
    );
  }

  getReportById<T>(id: number, accountId: number): Observable<ReportLayout<T>> {
    return this.httpClient.get<{ report_layout: ReportLayout<T> }>(`report_layouts/${id}`).pipe(
      map(({ report_layout }) => report_layout),
      map((r) => this.mapIncomingLayout(r)),
    );
  }

  getReportLayoutList(id: number, page: number, perPage: number): Observable<ReportLayoutListResponse> {
    const params = new HttpParams().set('page', page).set('per_page', perPage);

    return this.httpClient.get<ReportLayoutListResponse>(`report_layouts/${id}/list`, { params });
  }

  exportReportLayoutList(id: number, format: 'csv' | 'pdf' | 'xlsx'): Observable<{ message: string; async: boolean }> {
    return this.httpClient
      .get<ReportLayoutListExportResponse>(`report_layouts/${id}/list.${format}`)
      .pipe(map(({ data }) => data));
  }

  buildChartData(id: number, params: Partial<ReportLayoutChart>[], refresh = false): Observable<ReportLayoutChart[][]> {
    const url = `report_layouts/${id}/build_chart_data${refresh ? `?refresh=true` : ''}`;
    return this.httpClient.post<{ chart_data: any }>(url, { charts: [params] }).pipe(
      map((response) => {
        const chartData: ReportLayoutChart[][] = response?.chart_data ?? [[]];
        const sortVisualizationKeys = this.store.selectSnapshot(
          AccountState.isFeatureEnabled(FeatureFlag.SORT_VISUALIZATION_KEYS),
        );

        return chartData.map((charts) => (sortVisualizationKeys ? sortChartData(charts) : charts));
      }),
    );
  }

  delete<T>(id: number, accountId: number): Observable<ReportLayout<T>> {
    return this.httpClient.delete<any>(`report_layouts/${id}`).pipe(map(({ report_layout }) => report_layout));
  }

  getReportLayoutRelatedToTypesByAccount(accountId: string): Observable<ReportLayoutRelatedToType[]> {
    return this.httpClient
      .get<{ related_to_types: ReportLayoutRelatedToType[] }>(`report_layouts/related_to_types`)
      .pipe(map(({ related_to_types }) => related_to_types));
  }

  updateReportBuilderOptionsBadges(model: {
    allReportsCount: number;
    favoritesCount: number;
    byMeCount: number;
    reportBuildersCount: number;
    pdfReportsCount: number;
  }): void {
    const stgMenuItems = [...reportBuilderListMenuItems.value];
    const find = (id: string) => stgMenuItems.find((o) => o.id === id);

    const setBadge = (id: string, value: number) => {
      const item = find(id);
      if (item?.badge) item.badge.value = value.toString();
    };

    setBadge('all-reports', model.allReportsCount);
    setBadge('created_by_me', model.byMeCount);
    setBadge('favorites', model.favoritesCount);
    setBadge('report-builder', model.reportBuildersCount);
    setBadge('pdf-reports', model.pdfReportsCount);

    reportBuilderListMenuItems.next(stgMenuItems);
  }

  private transformAttributeProp(model: Partial<ReportLayout<any>>): Partial<ReportLayout<any>> {
    const value = { ...model };

    if (Object.keys(model).includes('report_layout_summary_formulas')) {
      value['report_layout_summary_formulas_attributes'] = [...value.report_layout_summary_formulas];
      delete value.report_layout_summary_formulas;
    }

    return value;
  }

  private mapIncomingSchedule(schedule: any): ExportScheduleForm {
    if (!schedule) {
      return schedule;
    }
    return {
      ...schedule,
      auto_send: schedule.enabled !== undefined ? schedule.enabled : schedule.auto_send,
    };
  }

  private mapOutgoingSchedule(schedule: any): any {
    if (!schedule) {
      return schedule;
    }
    const payload = { ...schedule };
    if (Object.prototype.hasOwnProperty.call(payload, 'auto_send')) {
      payload.enabled = payload.auto_send;
      delete payload.auto_send;
    }
    return payload;
  }

  private mapIncomingLayout<T>(layout: ReportLayout<T>): ReportLayout<T> {
    if (!layout) {
      return layout;
    }
    return {
      ...layout,
      auto_send: layout.enabled !== undefined ? layout.enabled : layout.auto_send,
    };
  }

  private mapOutgoingLayout<T>(layout: Partial<ReportLayout<T>>): Partial<ReportLayout<T>> {
    if (!layout) {
      return layout;
    }
    const payload = { ...layout };
    if (Object.prototype.hasOwnProperty.call(payload, 'auto_send')) {
      payload.enabled = payload.auto_send;
      delete payload.auto_send;
    }
    return payload;
  }
}
